import {
  AuthorisedUser,
  CallableType,
  ClearingHouseType,
  CollectionType,
  DateFormat,
  DateTerm,
  DealSettlement,
  DurationTerm,
  HaircutType,
  Order,
  OrderAttributes,
  OrderAttributesCreateSource,
  OrderCounterpartyAttributes,
  OrderTerm,
  OrderUserEnteredAttributes,
  ProductSector,
  RepoType,
  SponsorType,
  SpreadsheetMapping,
  SpreadsheetSettlementVenue,
  TradeConditionFlag,
  TradingEntity,
  ValidationIssue
} from 'api'
import { Locale, Translation } from 'locale'
import { groupBy, isDate, isString, uniq } from 'lodash-es'
import {
  CollectionTypeHelper,
  createInvalidValueValidationIssue,
  DateTermHelper,
  DurationTermHelper,
  generateUploadRowIndex,
  getDefaultCollectionName,
  getMaxCollectionSize,
  getUploadRowIndexPrefix,
  OrderTermHelper,
  spreadsheetSettlementVenueMapper
} from 'model'
import { compareInsensitive, createResultValidatorMapper, Decimal, ErrorResult, notNilFilter, NullResult, Result, ValueResult } from 'utils'
import { isNotNil } from 'utils/predicate'
import { invalidHaircutScenario, NOTE_INDEXES } from '../datagrid'
import {
  convertNumericStringValuesToNumber,
  gatherIssues,
  getEnumMappings,
  parseDate,
  parseDateValue,
  parseDecimal,
  parseDirectionWithNotionalAndStartConsideration,
  parseEnum,
  parseNumber,
  parseRate,
  parseString,
  removeAllSpacesFromString,
  SpreadsheetParseError,
  TypedSpreadsheetParseResult
} from '../upload/model'
import { getCounterpartyNamesFromGroups, getOnlyCounterpartyNames } from './parseOrderSpreadsheetHelpers'
import { parseUserEnteredEvergreenNoticePeriod, sanitizeParsedItems } from './utils'

export interface OrderSpreadsheetParseResult extends TypedSpreadsheetParseResult<OrderAttributes> {}

export function parseOrderSpreadsheet(
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping,
  authorisedUser: AuthorisedUser,
  getAvailableCollectionIdsByNameAndType: (name: string, type: CollectionType | null) => string[],
  getOrdersInCollection: (collectionId: string) => Order[],
  enabledCollectionTypes: CollectionType[],
  getClearingHouseIdByType: (clearingHouse: string) => string | null,
  getSponsorIdByType: (sponsor: string) => string | null,
  getProductSectorById: (collateralId: string) => ProductSector | null,
  allActiveTradingEntities: TradingEntity[]
): OrderSpreadsheetParseResult {
  const uploadRowIndexPrefix = getUploadRowIndexPrefix()

  const parseResults = rows.reduce(
    (spreadsheetResult, row, rowIdx) => {
      const rowResult = parseRow(
        filename,
        row,
        rowIdx + 1,
        spreadsheetMapping,
        authorisedUser,
        getAvailableCollectionIdsByNameAndType,
        enabledCollectionTypes,
        uploadRowIndexPrefix,
        getClearingHouseIdByType,
        getSponsorIdByType,
        getProductSectorById,
        allActiveTradingEntities
      )
      spreadsheetResult.issues.push(...rowResult.issues)
      spreadsheetResult.parsedItems.push(...rowResult.parsedItems)
      return spreadsheetResult
    },
    { filename, rows, issues: [], parsedItems: [] } as OrderSpreadsheetParseResult
  )

  return {
    ...parseResults,
    issues: [...parseResults.issues, ...getCollectionSizeValidationIssue(parseResults, getOrdersInCollection)],
    parsedItems: sanitizeParsedItems(parseResults.parsedItems)
  }
}

function parseRow(
  filename: string,
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  authorisedUser: AuthorisedUser,
  getAvailableCollectionIdsByNameAndType: (name: string, type: CollectionType | null) => string[],
  enabledCollectionTypes: CollectionType[],
  uploadRowIndexPrefix: string,
  getClearingHouseIdByType: (clearingHouse: string) => string | null,
  getSponsorIdByType: (sponsor: string) => string | null,
  getProductSectorById: (collateralId: string) => ProductSector | null,
  allActiveTradingEntities: TradingEntity[]
): OrderSpreadsheetParseResult {
  /* Collection Type */
  const collectionTypeResult = parseEnum<CollectionType>(
    row,
    rowNum,
    spreadsheetMapping.collectionType,
    spreadsheetMapping.collectionType.values,
    Locale.translate(Translation.TC_COLLECTION_TYPE)
  )

  /* Notional & Direction */
  const signedNotionalResult = parseDecimal(
    row,
    rowNum,
    spreadsheetMapping.notional,
    Locale.translate(Translation.TC_NOTIONAL, { amountFormat: null })
  )
  const signedStartConsiderationResult = parseNumber(
    row,
    rowNum,
    spreadsheetMapping.startConsideration,
    Locale.translate(Translation.TC_START_CONSIDERATION)
  )
  const directionResult = parseDirectionWithNotionalAndStartConsideration(
    row,
    rowNum,
    spreadsheetMapping.direction,
    Locale.translate(Translation.TC_DIRECTION),
    signedNotionalResult,
    signedStartConsiderationResult
  )
  const notionalResult = signedNotionalResult.map(signedValue => Math.abs(signedValue))
  const startConsiderationResult = signedStartConsiderationResult.map(signedValue => Math.abs(signedValue))

  /* Rate */
  const referenceRateResult = parseString(row, rowNum, spreadsheetMapping.referenceRate, Locale.translate(Translation.TC_REFERENCE_RATE))
  const rateResult = parseRate(row, rowNum, spreadsheetMapping.rate, Locale.translate(Translation.TC_RATE), referenceRateResult)

  /* Term */
  const spreadsheetTermResult = parseEnum<OrderTerm>(
    row,
    rowNum,
    spreadsheetMapping.term,
    getEnumMappings(OrderTerm, OrderTermHelper),
    Locale.translate(Translation.TC_TERM),
    true
  )

  /* Start Date Term */
  const startDateTermResult = parseEnum<DateTerm>(
    row,
    rowNum,
    spreadsheetMapping.startDate,
    getEnumMappings(DateTerm, DateTermHelper),
    Locale.translate(Translation.TC_START_DATE),
    true
  )

  /* Start Date */
  const startDateResult =
    (spreadsheetTermResult.isValue &&
      spreadsheetTermResult.value !== OrderTerm.CUSTOM &&
      spreadsheetTermResult.value !== OrderTerm.CUSTOM_O) ||
    startDateTermResult.isValue
      ? NullResult
      : parseDate(row, rowNum, spreadsheetMapping.startDate, Locale.translate(Translation.TC_START_DATE))

  /* End Date and Term */
  const spreadsheetEndDateStringResult = parseString(row, rowNum, spreadsheetMapping.endDate, Locale.translate(Translation.TC_END_DATE))
  const spreadsheetEndDateResult = parseDate(row, rowNum, spreadsheetMapping.endDate, Locale.translate(Translation.TC_END_DATE))
  const endDateAndTermResults = getEndDateAndTermResults(
    collectionTypeResult,
    spreadsheetEndDateStringResult,
    spreadsheetEndDateResult,
    spreadsheetTermResult,
    startDateResult,
    startDateTermResult,
    spreadsheetMapping,
    rowNum,
    Locale.translate(Translation.TC_END_DATE)
  )

  const fundResult = parseString(row, rowNum, spreadsheetMapping.fund, Locale.translate(Translation.TC_MY_ENTITY), true).map(name => {
    const found = allActiveTradingEntities.find(t => t.shortName === name || t.publishedName === name || t.id === name)
    if (found) return found.shortName
    return name
  })

  const isinBuiResult = parseString(row, rowNum, spreadsheetMapping.isin, Locale.translate(Translation.TC_ISIN_BUI), true)
  const secondaryIsinBuiResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.secondaryIsin,
    Locale.translate(Translation.TC_SECONDARY_ISIN_BUI)
  )
  const bookResult = parseString(row, rowNum, spreadsheetMapping.book, Locale.translate(Translation.TC_BOOK))
  const strategyResult = parseString(row, rowNum, spreadsheetMapping.strategy, Locale.translate(Translation.TC_STRATEGY))
  const primeBrokerResult = parseString(row, rowNum, spreadsheetMapping.primeBroker, Locale.translate(Translation.TC_PRIME_BROKER))
  const bundleResult = parseString(row, rowNum, spreadsheetMapping.bundle, Locale.translate(Translation.TC_BUNDLE))
  const roeResult = parseString(row, rowNum, spreadsheetMapping.rightOfExit, Locale.translate(Translation.TC_ROE))
  const rosResult = parseString(row, rowNum, spreadsheetMapping.rightOfSubstitution, Locale.translate(Translation.TC_ROS))
  const haircutTypeResult = parseEnum<HaircutType>(
    row,
    rowNum,
    spreadsheetMapping.haircutType,
    spreadsheetMapping.haircutType.values,
    Locale.translate(Translation.TC_HAIRCUT_TYPE)
  )
  const haircutResult = parseNumber(row, rowNum, spreadsheetMapping.haircut, Locale.translate(Translation.TC_HAIRCUT))
  const repoTypeResult = parseEnum<RepoType>(
    row,
    rowNum,
    spreadsheetMapping.repoType,
    spreadsheetMapping.repoType.values,
    Locale.translate(Translation.TC_REPO_TYPE)
  )
  const bondPriceResult = parseNumber(row, rowNum, spreadsheetMapping.bondPrice, Locale.translate(Translation.TC_COLLATERAL_PRICE))
  const positionIdResult = parseString(row, rowNum, spreadsheetMapping.externalId, Locale.translate(Translation.TC_POSITION_ID))

  /* Collection Name */
  const collectionNameResult = parseString(row, rowNum, spreadsheetMapping.collectionName, Locale.translate(Translation.TC_COLLECTION_NAME))

  /* Counterparty */
  const counterpartyNamesResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.counterpartyName,
    Locale.translate(Translation.TC_COUNTERPARTY)
  )
  const allCounterpartyNames = getCounterpartyNames(counterpartyNamesResult, authorisedUser, allActiveTradingEntities)
  const rollResult = parseString(row, rowNum, spreadsheetMapping.roll, Locale.translate(Translation.TC_ROLL))
  const unwindResult = parseString(row, rowNum, spreadsheetMapping.unwind, Locale.translate(Translation.TC_UNWIND))
  const subResult = parseString(row, rowNum, spreadsheetMapping.sub, Locale.translate(Translation.TC_SUB))
  const axeResult = parseString(row, rowNum, spreadsheetMapping.axe, Locale.translate(Translation.TC_AXE))

  const validateTradeCondition = createResultValidatorMapper<[string, string, string]>({
    errorMessage: _rollUnwindSubStrings => `Repeated counterparties between roll/unwind/sub not permitted.`,
    hasError: rollUnwindSubStrings => {
      const counterpartyNames = rollUnwindSubStrings.flatMap(tradeConditionString =>
        tradeConditionString
          .split(',')
          .map(v => v.trim())
          // the cells accept either Y or y or the counterparty/alias name so we get rid of the Y
          .filter(v => v.length > 0 && v.toLowerCase() !== 'y')
      )

      const duplicates = counterpartyNames.filter((item, index) => counterpartyNames.indexOf(item) !== index)

      return duplicates.length > 0
    }
  })

  const validateRepeatedConditions = createResultValidatorMapper<[string, string, string]>({
    errorMessage: rollUnwindSubStrings => {
      const repeatedConditions = [
        Locale.translate(Translation.TC_ROLL),
        Locale.translate(Translation.TC_SUB),
        Locale.translate(Translation.TC_UNWIND)
      ].filter((_, index) => rollUnwindSubStrings[index].toLocaleLowerCase() === 'y')
      return `Cannot have both ${repeatedConditions[0]} and ${repeatedConditions[1]} specified`
    },
    hasError: rollUnwindSubStrings => {
      return rollUnwindSubStrings.filter(v => v.toLowerCase() === 'y').length > 1
    }
  })

  const rollUnwindSubResults = new ValueResult([
    Result.getValue(rollResult, ''),
    Result.getValue(subResult, ''),
    Result.getValue(unwindResult, '')
  ])
    .map(validateTradeCondition)
    .map(validateRepeatedConditions)

  const csdResult = parseEnum<DealSettlement>(
    row,
    rowNum,
    spreadsheetMapping?.csd,
    spreadsheetMapping.csd?.values,
    Locale.translate(Translation.TC_CSD),
    true
  )

  const spreadsheetSettlementVenueResult = parseEnum<SpreadsheetSettlementVenue>(
    row,
    rowNum,
    spreadsheetMapping.settlementVenue,
    spreadsheetMapping.settlementVenue.values,
    Locale.translate(Translation.TC_SETTLEMENT_TYPE)
  )
  const clearingHouseResult = parseEnum<ClearingHouseType>(
    row,
    rowNum,
    spreadsheetMapping.clearingHouse,
    spreadsheetMapping.clearingHouse.values,
    Locale.translate(Translation.TC_CCP)
  )
  const sponsorResult = parseEnum<SponsorType>(
    row,
    rowNum,
    spreadsheetMapping.sponsor,
    spreadsheetMapping.sponsor.values,
    Locale.translate(Translation.TC_SPONSOR)
  )

  const preAgreedResult = parseString(row, rowNum, spreadsheetMapping.preAgreed, Locale.translate(Translation.TC_PRE_AGREED))

  const callableResult = parseEnum<CallableType>(
    row,
    rowNum,
    spreadsheetMapping.callableType,
    spreadsheetMapping.callableType.values,
    Locale.translate(Translation.TC_CALLABLE)
  )
  const callablePeriodResult = parseNumber(
    row,
    rowNum,
    spreadsheetMapping.callablePeriodDays,
    Locale.translate(Translation.TC_CALLABLE_PERIOD)
  )

  const evergreenNoticePeriodResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.evergreenNoticePeriod,
    Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD)
  )

  const noteResults: Result<string>[] = NOTE_INDEXES.map(noteIndex => {
    return parseString(
      row,
      rowNum,
      spreadsheetMapping.notes[noteIndex],
      Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(noteIndex, 10) + 1 })
    )
  })

  const orderCounterparties = getOrderCounterparties(
    allCounterpartyNames,
    rollUnwindSubResults,
    axeResult,
    primeBrokerResult,
    spreadsheetMapping
  )
  const negotiationInstruction = null
  const [collectionId, collectionName, collectionType] = getCollectionInfo(
    collectionNameResult,
    collectionTypeResult,
    getAvailableCollectionIdsByNameAndType,
    enabledCollectionTypes,
    authorisedUser,
    endDateAndTermResults.length > 1
  )

  const [spreadsheetSettlementVenue, clearingHouseId, sponsorId] = getSettlementDetails(
    spreadsheetSettlementVenueResult,
    clearingHouseResult,
    sponsorResult,
    getClearingHouseIdByType,
    getSponsorIdByType
  )

  const basketType = getProductSectorById(Result.getValue(isinBuiResult) || '')?.basketType

  const invalidHaircut = invalidHaircutScenario(
    basketType,
    isNotNil(spreadsheetSettlementVenue) ? spreadsheetSettlementVenueMapper(spreadsheetSettlementVenue) : null
  )

  const issues = gatherIssues(
    directionResult,
    notionalResult,
    startConsiderationResult,
    referenceRateResult,
    rateResult,
    startDateResult,
    ...endDateAndTermResults.map(([endDateResult, _endDateTermResult, _termResult]) => endDateResult),
    ...endDateAndTermResults.map(([_endDateResult, _endDateTermResult, termResult]) => termResult),
    ...endDateAndTermResults.flatMap(([, , , endDateTermsResults]) => endDateTermsResults),
    ...endDateAndTermResults.flatMap(([, , , , endDatesResults]) => endDatesResults),
    fundResult,
    isinBuiResult,
    secondaryIsinBuiResult,
    bookResult,
    strategyResult,
    primeBrokerResult,
    bundleResult,
    repoTypeResult,
    bondPriceResult,
    positionIdResult,
    collectionNameResult,
    collectionTypeResult,
    counterpartyNamesResult,
    rollResult,
    unwindResult,
    subResult,
    axeResult,
    spreadsheetSettlementVenueResult,
    clearingHouseResult,
    sponsorResult,
    preAgreedResult,
    csdResult,
    rollUnwindSubResults,
    roeResult,
    rosResult,
    haircutResult,
    haircutTypeResult,
    ...noteResults
  )
  if (issues.length === 0) {
    const orderUserEnteredAttributesList: OrderUserEnteredAttributes[] = endDateAndTermResults.map(
      ([endDateResult, endDateTermResult, termResult, endDateTermsResults, endDatesResults]) => ({
        direction: Result.getValue(directionResult),
        notional: Result.getValue(notionalResult),
        referenceRate: Result.getValue(referenceRateResult),
        rate: Result.getValue(rateResult),
        term: Result.getValue(termResult),
        startDate: Result.getValue(startDateResult),
        startDateTerm: Result.getValue(startDateTermResult),
        endDate: Result.getValue(endDateResult),
        endDateTerm: Result.getValue(endDateTermResult),
        endDateTerms: endDateTermsResults.length ? endDateTermsResults.flatMap(t => (t.isValue ? [t.value] : [])) : null,
        endDates: endDatesResults.length ? endDatesResults.flatMap(t => (t.isValue ? [t.value] : [])) : null,
        fundShortName: Result.getValue(fundResult),
        collateralId: Result.getValue(isinBuiResult),
        basketId: Result.getValue(isinBuiResult),
        secondaryCollateralId: null,
        secondaryBasketId: Result.getValue(secondaryIsinBuiResult),
        bondPrice: Result.getValue(bondPriceResult),
        startConsideration: Result.getValue(startConsiderationResult),
        repoType: Result.getValue(repoTypeResult),
        book: Result.getValue(bookResult),
        strategy: Result.getValue(strategyResult),
        bundle: Result.getValue(bundleResult),
        primeBrokerId: Result.getValue(primeBrokerResult),
        externalOrderId: null,
        positionId: Result.getValue(positionIdResult),
        currency: null,
        fundId: null,
        investmentDecisionMaker: null,
        executionDecisionMaker: null,
        callableType: Result.getValue(callableResult),
        callablePeriodDays: Result.getValue(callablePeriodResult),
        evergreenNoticePeriod: evergreenNoticePeriodResult.isValue
          ? parseUserEnteredEvergreenNoticePeriod(evergreenNoticePeriodResult.value)
          : null,
        noticePeriodDays: null,
        backToStagingTradingType: null,
        blockTrade: null,
        rfqType: null,
        notes: noteResults.reduce<Record<string, string>>((acc, noteResult, index) => {
          if (noteResult.isValue) {
            acc[index.toString()] = noteResult.value
          }
          return acc
        }, {}),
        allowPartial: false,
        targetFillNotional: null,
        targetFillStartConsideration: null,
        uploadRowIndex: generateUploadRowIndex(uploadRowIndexPrefix, rowNum),
        preAgreed: preAgreedResult.isValue && preAgreedResult.value.toLocaleLowerCase() === 'y',
        masterLegalAgreementType: null,
        csd: Result.getValue(csdResult),
        settlementVenue: isNotNil(spreadsheetSettlementVenue) ? spreadsheetSettlementVenueMapper(spreadsheetSettlementVenue) : null,
        clearingHouseId,
        sponsorId,
        startConsiderationOverride: false,
        fee: null,
        stockBorrowingLendingType: null,
        dateSource: null,
        parentOrderId: null,
        rightOfExit: roeResult.isValue ? (roeResult.value.toLocaleLowerCase() === 'n' ? 0 : 1) : 1,
        rightOfSubstitution: rosResult.isValue ? (rosResult.value.toLocaleLowerCase() === 'n' ? 0 : 1) : 1,
        haircutType: getHaircutSanitized(invalidHaircut, Result.getValue(haircutTypeResult)),
        haircut: getHaircutSanitized(invalidHaircut, haircutResult.isValue ? Decimal.shift(haircutResult.value, -2) : null!)
      })
    )

    return {
      filename,
      rows: [row],
      issues,
      parsedItems: orderUserEnteredAttributesList.map(orderUserEnteredAttributes => ({
        orderUserEnteredAttributes,
        source: OrderAttributesCreateSource.SPREADSHEET_UPLOAD,
        orderCounterparties,
        negotiationInstruction,
        collectionId,
        collectionName,
        collectionType
      }))
    }
  } else {
    return { filename, rows: [row], issues, parsedItems: [] }
  }
}

function getHaircutSanitized<T>(invalidHaircut: boolean, haircut: T): T | null {
  if (invalidHaircut) return null
  return haircut
}

function getEndDateResult(
  endDate: any,
  dateFormat: DateFormat | null,
  rowNum: number,
  fieldName: string
): [Result<Date>, Result<DurationTerm>] {
  const enumValues = getEnumMappings(DurationTerm, DurationTermHelper)
  const matched = Object.keys(enumValues).find(key => compareInsensitive(enumValues[key], endDate, true) === 0)
  if (matched) {
    return [NullResult, new ValueResult(matched as DurationTerm)]
  }

  try {
    const parsedEndDate = parseDateValue(endDate, dateFormat)
    const isEnum = !isDate(parsedEndDate)
    return [isEnum ? NullResult : new ValueResult(parsedEndDate), !isEnum ? NullResult : new ValueResult(parsedEndDate)]
  } catch (err) {
    return [
      new ErrorResult(new SpreadsheetParseError(err instanceof Error ? err.message : isString(err) ? err : '', rowNum, fieldName)),
      NullResult
    ]
  }
}

function getEndDateAndTermResults(
  collectionTypeResult: Result<CollectionType>,
  spreadsheetEndDateStringResult: Result<string>,
  spreadsheetEndDateResult: Result<Date>,
  spreadsheetTermResult: Result<OrderTerm>,
  startDateResult: Result<Date>,
  startDateTermResult: Result<DateTerm>,
  spreadsheetMapping: SpreadsheetMapping,
  rowNum: number,
  fieldName: string
): [Result<Date>, Result<DurationTerm>, Result<OrderTerm>, Result<DurationTerm>[], Result<Date>[]][] {
  if (collectionTypeResult.isValue && collectionTypeResult.value === CollectionType.ALL_OR_NOTHING_X) {
    if (spreadsheetTermResult.isValue && spreadsheetTermResult.value !== OrderTerm.CUSTOM) {
      return [[NullResult, NullResult, spreadsheetTermResult, [], []]]
    }

    const aonxResults = parseEndDateTermsResult(spreadsheetEndDateStringResult, rowNum, spreadsheetMapping)

    return [[NullResult, NullResult, new ValueResult(OrderTerm.CUSTOM), aonxResults.endDateTermsResults, aonxResults.endDatesResults]]
  }

  // If term is not CUSTOM, then discard any end dates
  if (spreadsheetTermResult.isValue && spreadsheetTermResult.value !== OrderTerm.CUSTOM) {
    return [[NullResult, NullResult, spreadsheetTermResult, [], []]]
  }

  // If end date cell value can be parsed directly as a Date object, then use that
  // This is for when the excel stores the date as integer, i.e. see excelDateToDate in parseSpreadsheetUtils
  if (spreadsheetEndDateResult.isValue) {
    return [[spreadsheetEndDateResult, NullResult, new ValueResult(OrderTerm.CUSTOM), [], []]]
  }

  // If end date cell value is null, then return null end date result
  // This is when we upload open deals without end date (CUSTOM_O)
  if (!spreadsheetEndDateStringResult.isValue) {
    const termResult =
      spreadsheetTermResult.isNull && (startDateResult.isValue || startDateTermResult.isValue)
        ? new ValueResult(OrderTerm.CUSTOM_O)
        : spreadsheetTermResult
    return [[NullResult, NullResult, termResult, [], []]]
  }

  const spreadsheetEndDates = removeAllSpacesFromString(spreadsheetEndDateStringResult.value).split(',')
  const dateFormat = spreadsheetMapping.endDate.dateFormat

  if (!spreadsheetTermResult.isValue) {
    return spreadsheetEndDates.map(endDate => {
      const [endDateResult, endDateTermResult] = getEndDateResult(endDate, dateFormat, rowNum, fieldName)
      const termResult =
        startDateResult.isValue || endDateResult.isValue || startDateTermResult.isValue || endDateTermResult.isValue
          ? endDateTermResult.isValue && endDateTermResult.value === DurationTerm.O
            ? new ValueResult(OrderTerm.CUSTOM_O)
            : new ValueResult(OrderTerm.CUSTOM)
          : spreadsheetTermResult
      return [endDateResult, endDateTermResult, termResult, [], []]
    })
  }

  // At this point, term must be CUSTOM, only a single end date is allowed
  if (spreadsheetEndDates.length !== 1) {
    return [[NullResult, NullResult, spreadsheetTermResult, [], []]]
  } else {
    const [endDateResult, endDateTermResult] = getEndDateResult(spreadsheetEndDates[0], dateFormat, rowNum, fieldName)
    return [[endDateResult, endDateTermResult, spreadsheetTermResult, [], []]]
  }
}

function parseEndDateTermsResult(
  spreadsheetEndStateStringResult: Result<string>,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping
): { endDatesResults: Result<Date>[]; endDateTermsResults: Result<DurationTerm>[] } {
  if (spreadsheetEndStateStringResult.isValue && typeof spreadsheetMapping.endDate.heading === 'string') {
    const dateFormat = spreadsheetMapping.endDate.dateFormat
    const termsOrEndDates = removeAllSpacesFromString(spreadsheetEndStateStringResult.value)
      .split(',')
      .map(endDate => convertNumericStringValuesToNumber(endDate))

    const isDate = (value: any) => {
      try {
        return !!parseDateValue(value, dateFormat)
      } catch (err) {
        return false
      }
    }

    if (termsOrEndDates.every(isDate)) {
      const endDatesResults = termsOrEndDates.map(date => {
        return parseDate(
          { [spreadsheetMapping.endDate.heading as string]: date },
          rowNum,
          spreadsheetMapping.endDate,
          Locale.translate(Translation.TC_END_DATE),
          true
        )
      })

      return { endDatesResults, endDateTermsResults: [] }
    } else {
      const endDateTermsResults = termsOrEndDates.map(term => {
        return parseEnum<DurationTerm>(
          { [spreadsheetMapping.endDate.heading as string]: term },
          rowNum,
          { heading: spreadsheetMapping.endDate.heading },
          getEnumMappings(DurationTerm, DurationTermHelper),
          Locale.translate(Translation.TC_END_DATE),
          true
        )
      })

      return { endDatesResults: [], endDateTermsResults }
    }
  }

  return { endDatesResults: [], endDateTermsResults: [] }
}

// Tries looking up any existing collection with specified name and optionally type.
// = 0 found - create new collection with name and type (AONX collections are always send via this option)
// = 1 found - use the collection
// > 1 found - use the first in the list which would be the most recent collection with that name and type
function getCollectionInfo(
  collectionNameResult: Result<string>,
  collectionTypeResult: Result<CollectionType>,
  getAvailableCollectionIdsByNameAndType: (name: string, type: CollectionType | null) => string[],
  enabledCollectionTypes: CollectionType[],
  authorisedUser: AuthorisedUser,
  multipleEndDates: boolean
): [string | null, string | null, CollectionType | null] {
  const collectionName = collectionNameResult.isValue ? collectionNameResult.value : null
  const collectionType = getCollectionType(collectionNameResult, collectionTypeResult, enabledCollectionTypes, null)
  const collectionIds = collectionName ? getAvailableCollectionIdsByNameAndType(collectionName, collectionType) : []

  if (multipleEndDates) {
    return [
      null,
      collectionName ?? getDefaultCollectionName(authorisedUser, CollectionType.ONE_CANCELS_OTHERS),
      CollectionType.ONE_CANCELS_OTHERS
    ]
  }

  return collectionIds.length === 0 || collectionType === CollectionType.ALL_OR_NOTHING_X
    ? [null, collectionName, getCollectionType(collectionNameResult, collectionTypeResult, enabledCollectionTypes)]
    : [collectionIds[0], collectionName, getCollectionType(collectionNameResult, collectionTypeResult, enabledCollectionTypes)]
}

function getCollectionType(
  collectionNameResult: Result<string>,
  collectionTypeResult: Result<CollectionType>,
  enabledCollectionTypes: CollectionType[],
  collectionTypeDefault: CollectionType | null = CollectionType.SEND_INDIVIDUALLY
) {
  if (!collectionNameResult.isValue) {
    return null
  }

  if (collectionTypeResult.isValue) {
    return enabledCollectionTypes.includes(collectionTypeResult.value) ? collectionTypeResult.value : CollectionType.SEND_INDIVIDUALLY
  } else {
    return collectionTypeDefault
  }
}

function isRollUnwindAxe(counterpartyName: string, rollUnwindAxe: string | null) {
  return rollUnwindAxe && (rollUnwindAxe.toLocaleLowerCase() === 'y' || rollUnwindAxe === counterpartyName)
}

function getTradeConditionFlagByString({
  counterpartyName,
  roll,
  sub,
  unwind
}: {
  counterpartyName: string
  roll: string | null
  sub: string | null
  unwind: string | null
}) {
  if (isRollUnwindAxe(counterpartyName, roll)) {
    return TradeConditionFlag.ROLL
  } else if (isRollUnwindAxe(counterpartyName, sub)) {
    return TradeConditionFlag.SUB
  } else if (isRollUnwindAxe(counterpartyName, unwind)) {
    return TradeConditionFlag.UNWIND
  } else {
    return TradeConditionFlag.NONE
  }
}

function getTradeConditionFlagByBool({ isRoll, isSub, isUnwind }: { isRoll: boolean; isSub: boolean; isUnwind: boolean }) {
  if (isRoll) {
    return TradeConditionFlag.ROLL
  } else if (isSub) {
    return TradeConditionFlag.SUB
  } else if (isUnwind) {
    return TradeConditionFlag.UNWIND
  } else {
    return TradeConditionFlag.NONE
  }
}

function getOrderCounterparties(
  allCounterpartyNames: string[],
  rollUnwindSubResults: Result<[string, string, string]>,
  axeResult: Result<string>,
  primeBrokerResult: Result<string>,
  spreadsheetMapping: SpreadsheetMapping
): OrderCounterpartyAttributes[] {
  const [rollsString, subsString, unwindsString] = Result.getValue(rollUnwindSubResults, ['', '', ''])
  const rolls = rollsString.split(',').map(v => v.trim())
  const unwinds = unwindsString.split(',').map(v => v.trim())
  const subs = subsString.split(',').map(v => v.trim())

  const axes = Result.getValue(axeResult, '')
    .split(',')
    .map(v => v.trim())

  const counterpartyNames = uniq(
    [...allCounterpartyNames, ...rolls, ...unwinds, ...subs, ...axes].filter(notNilFilter).map(counterpartyName => counterpartyName.trim())
  ).filter(v => v.length > 0)

  // If there is a single counterparty in the Counterparty Name column, and only one counterparty
  // across all counterparty related columns, assume Y/y is yes
  if (
    allCounterpartyNames.length === 1 &&
    counterpartyNames.filter(counterpartyName => counterpartyName.toLocaleLowerCase() !== 'y').length === 1
  ) {
    return [
      {
        axeId: null,
        internalAxeId: null,
        counterpartyName: allCounterpartyNames[0],
        counterpartyOrganisationShortCode: null,
        tradeConditionFlag: getTradeConditionFlagByString({
          counterpartyName: allCounterpartyNames[0],
          roll: rolls[0],
          sub: subs[0],
          unwind: unwinds[0]
        }),
        axed: isRollUnwindAxe(allCounterpartyNames[0], axes[0]) ? 'Axed' : null,
        primeBrokerId: primeBrokerResult.isValue ? primeBrokerResult.value : null,
        masterLegalAgreementType: null,
        repoType: null,
        source: spreadsheetMapping.counterpartyName.heading
      }
    ]
  }

  // If there are no counterparties in the Counterparty Name column or more than one counterparty across the
  // counterparty related columns, assume Y/y is a counterparty name or alias
  return counterpartyNames.map(counterpartyName => {
    const isRoll = rolls.includes(counterpartyName)
    const isUnwind = unwinds.includes(counterpartyName)
    const isSub = subs.includes(counterpartyName)
    const isAxe = axes.includes(counterpartyName)

    let source: string | null = null

    if (allCounterpartyNames.includes(counterpartyName)) {
      source = spreadsheetMapping.counterpartyName.heading
    } else if (isRoll) {
      source = spreadsheetMapping.roll.heading
    } else if (isUnwind) {
      source = spreadsheetMapping.unwind.heading
    } else if (isSub) {
      source = spreadsheetMapping.sub.heading
    } else if (isAxe) {
      source = spreadsheetMapping.axe.heading
    }

    return {
      counterpartyName: counterpartyName,
      counterpartyOrganisationShortCode: null,
      tradeConditionFlag: getTradeConditionFlagByBool({ isRoll, isUnwind, isSub }),
      axeId: null,
      internalAxeId: null,
      axed: isAxe ? 'Axed' : null,
      primeBrokerId: Result.getValue(primeBrokerResult),
      masterLegalAgreementType: null,
      repoType: null,
      source
    }
  })
}

function getCounterpartyNames(
  counterpartyNamesResult: Result<string>,
  authorisedUser: AuthorisedUser,
  allActiveTradingEntities: TradingEntity[]
) {
  const groupNames = authorisedUser.userPreference.counterpartyGroups.map(counterpartyGroup => counterpartyGroup.description)
  const groupAndCounterpartyNamesFromSpreadsheet = counterpartyNamesResult.isValue ? counterpartyNamesResult.value.split(',') : []
  const counterpartyNames = getOnlyCounterpartyNames(groupAndCounterpartyNamesFromSpreadsheet, groupNames)
  const counterpartyNamesFromGroups = getCounterpartyNamesFromGroups(
    groupAndCounterpartyNamesFromSpreadsheet,
    groupNames,
    authorisedUser,
    allActiveTradingEntities
  )

  return [...counterpartyNames, ...counterpartyNamesFromGroups]
}

function getSettlementDetails(
  settlementVenueResult: Result<SpreadsheetSettlementVenue>,
  clearingHouseResult: Result<ClearingHouseType>,
  sponsorResult: Result<SponsorType>,
  getClearingHouseIdByType: (clearingHouse: string) => string | null,
  getSponsorIdByType: (sponsor: string) => string | null
): [SpreadsheetSettlementVenue | null, string | null, string | null] {
  const settlementVenue = settlementVenueResult.isValue ? settlementVenueResult.value : null
  const clearingHouseId =
    settlementVenue === null || settlementVenue === SpreadsheetSettlementVenue.Bilateral
      ? null
      : clearingHouseResult.isValue
      ? getClearingHouseIdByType(clearingHouseResult.value)
      : null
  const sponsorId =
    settlementVenue === SpreadsheetSettlementVenue.Direct || clearingHouseId === null
      ? null
      : sponsorResult.isValue
      ? getSponsorIdByType(sponsorResult.value)
      : null

  return [settlementVenue, clearingHouseId, sponsorId]
}

function getValidationCollectionName(order: OrderAttributes) {
  return `${order.collectionName} (${CollectionTypeHelper.getName(order.collectionType ?? CollectionType.SEND_INDIVIDUALLY)})`
}

function getCollectionSizeValidationIssue(
  parseResult: OrderSpreadsheetParseResult,
  getOrdersInCollection: (collectionId: string) => Order[]
) {
  const validationIssues: ValidationIssue[] = []
  if (!parseResult || parseResult.parsedItems.length === 0) {
    return validationIssues
  }
  const ordersByCollectionId = groupBy(
    parseResult.parsedItems.filter(order => order.collectionId !== null),
    order => order.collectionId
  )
  Object.entries(ordersByCollectionId).forEach(([collectionId, orders]) => {
    const collectionType = orders?.[0]?.collectionType
    const maxCollectionSize = getMaxCollectionSize(collectionType)

    const maxLegCount = Decimal.subtract(maxCollectionSize, getOrdersInCollection(collectionId).length)
    if (orders.length > maxLegCount) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          Locale.translate(Translation.TC_COLLECTION_NAME),
          Locale.translate(Translation.SC_MAX_NAMED_COLLECTION_SIZE_TOOLTIP, {
            collectionName: getValidationCollectionName(orders[0]),
            remainingLegs: maxLegCount,
            maxLegs: maxCollectionSize
          })
        )
      )
    }
  })

  const ordersByCollectionName = groupBy(
    parseResult.parsedItems.filter(order => order.collectionName !== null),
    order => getValidationCollectionName(order)
  )
  Object.entries(ordersByCollectionName).forEach(([collectionName, orders]) => {
    const collectionType = orders?.[0]?.collectionType
    const maxCollectionSize = getMaxCollectionSize(collectionType)

    if (orders.length > maxCollectionSize) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          Locale.translate(Translation.TC_COLLECTION_NAME),
          Locale.translate(Translation.SC_MAX_NAMED_COLLECTION_SIZE_TOOLTIP, {
            collectionName,
            remainingLegs: maxCollectionSize,
            maxLegs: maxCollectionSize
          })
        )
      )
    }
  })

  return validationIssues
}
